import { __assign, __rest } from "tslib";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { nodeContains } from '../internal/utils/dom';
import useFocusVisible from '../internal/hooks/focus-visible';
import { useContainerQuery } from '../internal/hooks/container-queries';
import { useUniqueId } from '../internal/hooks/use-unique-id';
import ChartPopover from '../internal/components/chart-popover';
import ChartSeriesDetails from '../internal/components/chart-series-details';
import { ChartScale, NumericChartScale } from './scales';
import { makeScaledSeries, createTicks, getBarGroups, LEFT_LABELS_MARGIN, computeDomainX, computeDomainY, focusSVGElement } from './utils';
import AxisLabel from './axis-label';
import VerticalMarkers from './vertical-marker';
import { EmphasizedBaseline, VerticalGridLines } from './grid';
import { BottomLabels, LeftHiddenLabels, LeftLabels } from './labels';
import DataSeries from './data-series';
import { useMouseHover } from './hooks/use-mouse-hover';
import { useNavigation } from './hooks/use-navigation';
import { useFocusHighlight } from './hooks/use-focus-highlight';
import { usePopover } from './hooks/use-popover';
import styles from './styles.css.js';
export default function ChartContainer(_a) {
    var _b, _c, _d;
    var _e;
    var innerHeight = _a.height, series = _a.series, visibleSeries = _a.visibleSeries, highlightedSeries = _a.highlightedSeries, onHighlightChange = _a.onHighlightChange, _f = _a.detailPopoverSize, detailPopoverSize = _f === void 0 ? 'medium' : _f, _g = _a.stackedBars, stackedBars = _g === void 0 ? false : _g, _h = _a.horizontalBars, horizontalBars = _h === void 0 ? false : _h, xScaleType = _a.xScaleType, yScaleType = _a.yScaleType, xTickFormatter = _a.xTickFormatter, yTickFormatter = _a.yTickFormatter, emphasizeBaselineAxis = _a.emphasizeBaselineAxis, xTitle = _a.xTitle, yTitle = _a.yTitle, ariaLabel = _a.ariaLabel, ariaLabelledby = _a.ariaLabelledby, ariaDescription = _a.ariaDescription, _j = _a.i18nStrings, i18nStrings = _j === void 0 ? {} : _j, props = __rest(_a, ["height", "series", "visibleSeries", "highlightedSeries", "onHighlightChange", "detailPopoverSize", "stackedBars", "horizontalBars", "xScaleType", "yScaleType", "xTickFormatter", "yTickFormatter", "emphasizeBaselineAxis", "xTitle", "yTitle", "ariaLabel", "ariaLabelledby", "ariaDescription", "i18nStrings"]);
    var focusVisible = useFocusVisible();
    var svgRef = useRef(null);
    var verticalMarkerRef = useRef(null);
    var descriptionId = useUniqueId('awsui-mixed-line-bar-chart__description');
    var _k = useContainerQuery(function (rect) { return rect.width; }), leftLabelsWidth = _k[0], leftLabelsRef = _k[1];
    var _l = useContainerQuery(function (rect) { return rect.width; }), containerWidth = _l[0], containerRef = _l[1];
    var innerWidth = leftLabelsWidth && containerWidth ? containerWidth - leftLabelsWidth - LEFT_LABELS_MARGIN : 500;
    var xDomain = (props.xDomain || computeDomainX(series, xScaleType));
    var yDomain = (props.yDomain || computeDomainY(series, stackedBars));
    var linesOnly = series.every(function (_a) {
        var series = _a.series;
        return series.type === 'line' || series.type === 'threshold';
    });
    var xScale = new ChartScale(xScaleType, xDomain, [0, horizontalBars ? innerHeight : innerWidth], linesOnly);
    var yScale = new NumericChartScale(yScaleType, yDomain || [0, 1], horizontalBars ? [0, innerWidth] : [innerHeight, 0]);
    // The number of ticks is currently defined by the length of the scale.
    // The x scale tends to have longer labels, so we're using less ticks for it.
    // These numbers are currently based on first impressions and might change in the future.
    // We might also open up an API in the future to control the amount of ticks displayed.
    var xTicks = createTicks(xScale, Math.ceil(innerWidth / 100));
    var yTicks = yScale.d3Scale.ticks(Math.ceil(innerHeight / 50));
    if (yScale.scaleType === 'log' && yTicks.length > 10) {
        // The logarithmic scale sometimes produces a very large amount of (major and minor) ticks,
        // at which point we need to reduce them significantly for space.
        yTicks = yScale.d3Scale.ticks(3);
    }
    /**
     * Interactions
     */
    var focusOutlineRef = useRef(null);
    var highlightedPointRef = useRef(null);
    var focusedSeriesRef = useRef(null);
    var barGroupsRef = useRef(null);
    var scaledSeries = makeScaledSeries(visibleSeries, xScale, yScale);
    var barGroups = getBarGroups(visibleSeries, xScale);
    var _m = useState(null), highlightedPoint = _m[0], setHighlightedPoint = _m[1];
    var _o = useState(null), highlightedGroupIndex = _o[0], setHighlightedGroupIndex = _o[1];
    var _p = usePopover({
        visibleSeries: visibleSeries,
        xTickFormatter: xTickFormatter
    }), isPopoverOpen = _p.isPopoverOpen, isPopoverPinned = _p.isPopoverPinned, showPopover = _p.showPopover, hidePopover = _p.hidePopover, pinPopover = _p.pinPopover, popoverProps = _p.popoverProps, popoverDetails = _p.popoverDetails, popoverData = _p.popoverData;
    var highlightPoint = useCallback(function (point) {
        setHighlightedGroupIndex(null);
        setHighlightedPoint(point);
    }, []);
    var highlightPointByIndex = function (series, index) {
        var _a;
        if (series.type === 'line') {
            if (index < 0 || index > series.data.length - 1) {
                return;
            }
            var datum_1 = series.data[index];
            var _b = scaledSeries.filter(function (s) { return s.datum === datum_1; })[0], x = _b.x, y = _b.y, color = _b.color;
            highlightPoint({ x: x, y: y, color: color, datum: datum_1, series: series });
        }
        else if (series.type === 'threshold') {
            var x = (highlightedPoint === null || highlightedPoint === void 0 ? void 0 : highlightedPoint.x) || 0;
            var y = series.y, _c = series.color, color = _c === void 0 ? '' : _c;
            highlightPoint({ x: x, y: (_a = yScale.d3Scale(y)) !== null && _a !== void 0 ? _a : NaN, color: color, series: series });
        }
    };
    var highlightSeries = useCallback(function (series) {
        if (series !== highlightedSeries) {
            onHighlightChange(series);
        }
    }, [highlightedSeries, onHighlightChange]);
    var highlightGroup = useCallback(function (groupIndex) {
        highlightSeries(null);
        setHighlightedPoint(null);
        setHighlightedGroupIndex(groupIndex);
        if (barGroupsRef.current) {
            var trigger = barGroupsRef.current.querySelector(":nth-child(" + (groupIndex + 1) + ")");
            if (trigger) {
                showPopover({
                    x: xDomain[groupIndex],
                    series: null,
                    trigger: trigger
                });
            }
        }
    }, [highlightSeries, showPopover, xDomain]);
    var clearHighlightedSeries = useCallback(function () {
        highlightSeries(null);
        setHighlightedGroupIndex(null);
        setHighlightedPoint(null);
        hidePopover();
    }, [hidePopover, highlightSeries]);
    var _q = useNavigation({
        series: series,
        visibleSeries: visibleSeries,
        scaledSeries: scaledSeries,
        barGroups: barGroups,
        barGroupsRef: barGroupsRef,
        xScale: xScale,
        yScale: yScale,
        highlightedPoint: highlightedPoint,
        highlightedGroupIndex: highlightedGroupIndex,
        highlightedSeries: highlightedSeries,
        isPopoverPinned: isPopoverPinned,
        pinPopover: pinPopover,
        highlightSeries: highlightSeries,
        highlightGroup: highlightGroup,
        highlightPoint: highlightPoint,
        highlightPointByIndex: highlightPointByIndex,
        clearHighlightedSeries: clearHighlightedSeries
    }), isGroupNavigation = _q.isGroupNavigation, lineSeriesProps = _q.lineSeriesProps, barGroupProps = _q.barGroupProps;
    var _r = useMouseHover({
        scaledSeries: scaledSeries,
        svgRef: svgRef,
        highlightPoint: highlightPoint,
        isGroupNavigation: isGroupNavigation,
        isPopoverPinned: isPopoverPinned
    }), onSVGMouseMove = _r.onSVGMouseMove, onSVGMouseOut = _r.onSVGMouseOut, verticalMarkerLeft = _r.verticalMarkerLeft;
    useFocusHighlight({
        containerRef: svgRef,
        focusOutlineRef: focusOutlineRef,
        highlightedPointRef: highlightedPointRef,
        highlightedPoint: highlightedPoint,
        highlightedGroupIndex: highlightedGroupIndex
    });
    // Popover for vertical markers (all series)
    useEffect(function () {
        if (isGroupNavigation || isPopoverPinned || highlightedPoint) {
            return;
        }
        if (isPopoverOpen && (popoverData === null || popoverData === void 0 ? void 0 : popoverData.verticalMarkerLeft) === verticalMarkerLeft) {
            return;
        }
        if (!verticalMarkerRef.current && isPopoverOpen) {
            // Open popover is attached to an invalid trigger. Hide it.
            hidePopover();
            return;
        }
        var newPopoverData = null;
        // Popover for all points on the vertical line
        if (verticalMarkerLeft &&
            !highlightedPoint &&
            verticalMarkerRef.current &&
            (popoverData === null || popoverData === void 0 ? void 0 : popoverData.verticalMarkerLeft) !== verticalMarkerLeft) {
            // Find the correct x data point for the vertical marker
            var dataX = null;
            for (var i = 0; i < scaledSeries.length; i++) {
                var _a = scaledSeries[i], x = _a.x, datum = _a.datum;
                if (x === verticalMarkerLeft && datum) {
                    dataX = datum.x;
                    break;
                }
            }
            if (dataX !== null) {
                newPopoverData = {
                    x: dataX,
                    verticalMarkerLeft: verticalMarkerLeft,
                    trigger: verticalMarkerRef.current,
                    series: null
                };
            }
        }
        if (newPopoverData) {
            showPopover(newPopoverData);
        }
        else if (isPopoverOpen) {
            hidePopover();
        }
    }, [
        hidePopover,
        highlightedPoint,
        isGroupNavigation,
        isPopoverOpen,
        isPopoverPinned,
        popoverData,
        scaledSeries,
        showPopover,
        verticalMarkerLeft
    ]);
    // Popover for one highlighted series point
    useEffect(function () {
        var _a, _b, _c;
        if (isGroupNavigation || highlightedPoint === null) {
            return;
        }
        if (!highlightedPointRef.current) {
            if (isPopoverOpen) {
                // Open popover is attached to an invalid trigger. Hide it.
                hidePopover();
            }
            return;
        }
        var datumX = (_b = (_a = highlightedPoint === null || highlightedPoint === void 0 ? void 0 : highlightedPoint.datum) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : null;
        if ((popoverData === null || popoverData === void 0 ? void 0 : popoverData.x) === datumX && ((_c = popoverData.series) === null || _c === void 0 ? void 0 : _c.series) === highlightedPoint.series) {
            return;
        }
        var seriesToFind = highlightedSeries || highlightedPoint.series;
        var internalSeries = visibleSeries.filter(function (_a) {
            var series = _a.series;
            return series === seriesToFind;
        })[0];
        if (internalSeries) {
            showPopover({
                x: datumX,
                trigger: highlightedPointRef.current,
                series: internalSeries
            });
        }
    }, [
        showPopover,
        highlightedPoint,
        highlightedSeries,
        isGroupNavigation,
        visibleSeries,
        popoverData,
        isPopoverOpen,
        isPopoverPinned,
        hidePopover
    ]);
    useEffect(function () {
        if (containerRef.current &&
            focusedSeriesRef.current &&
            nodeContains(containerRef.current, document.activeElement)) {
            var target = focusedSeriesRef.current;
            focusSVGElement(target);
        }
    }, [containerRef, focusedSeriesRef, highlightedSeries]);
    var onBlur = useCallback(function (event) {
        var blurTarget = event.relatedTarget || event.target;
        if (blurTarget === null || !(blurTarget instanceof Element) || !nodeContains(containerRef.current, blurTarget)) {
            clearHighlightedSeries();
            if (isPopoverOpen && !isPopoverPinned) {
                hidePopover();
            }
        }
    }, [clearHighlightedSeries, containerRef, hidePopover, isPopoverOpen, isPopoverPinned]);
    var onSVGClick = function () {
        if (isPopoverOpen) {
            if (isPopoverPinned) {
                hidePopover();
            }
            else {
                pinPopover();
            }
        }
    };
    var xOffset = xScale.isCategorical() ? Math.max(0, xScale.d3Scale.bandwidth() - 1) / 2 : 0;
    var verticalLineX = null;
    if (verticalMarkerLeft !== null) {
        verticalLineX = verticalMarkerLeft;
    }
    else if (isGroupNavigation && highlightedGroupIndex !== null) {
        var x = (_e = xScale.d3Scale(barGroups[highlightedGroupIndex].x)) !== null && _e !== void 0 ? _e : null;
        if (x !== null) {
            verticalLineX = xOffset + x;
        }
    }
    return (React.createElement("div", { className: styles['chart-container'], ref: containerRef, onBlur: onBlur },
        React.createElement(AxisLabel, { title: horizontalBars ? xTitle : yTitle, position: "left", horizontalBars: horizontalBars }),
        React.createElement("div", { className: styles['chart-container__horizontal'] },
            React.createElement(LeftHiddenLabels, { labelsRef: leftLabelsRef, ticks: horizontalBars ? xTicks : yTicks, scale: horizontalBars ? xScale : yScale, tickFormatter: horizontalBars ? xTickFormatter : yTickFormatter, horizontalBars: horizontalBars }),
            React.createElement("div", { className: styles['chart-container__vertical'] },
                React.createElement("svg", { width: innerWidth, height: innerHeight, ref: svgRef, className: clsx(styles.svg, (_b = {},
                        // Show cursor when the popover can be pinned
                        _b[styles['chart--clickable']] = isPopoverOpen && !isPopoverPinned,
                        _b)), role: "graphics-document", "aria-hidden": "false", "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-describedby": ariaDescription ? descriptionId : undefined, "aria-roledescription": i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.chartAriaRoleDescription, onMouseMove: onSVGMouseMove, onMouseOut: onSVGMouseOut, onClick: onSVGClick, focusable: false },
                    ariaDescription && (React.createElement("desc", { "aria-hidden": "true", id: descriptionId }, ariaDescription)),
                    React.createElement(LeftLabels, { ticks: horizontalBars ? xTicks : yTicks, scale: horizontalBars ? xScale : yScale, tickFormatter: horizontalBars ? xTickFormatter : yTickFormatter, title: horizontalBars ? xTitle : yTitle, ariaRoleDescription: horizontalBars ? i18nStrings.xAxisAriaRoleDescription : i18nStrings.yAxisAriaRoleDescription, horizontalBars: horizontalBars, width: innerWidth }),
                    horizontalBars && React.createElement(VerticalGridLines, { scale: yScale, ticks: yTicks, innerHeight: innerHeight }),
                    React.createElement(DataSeries, { innerWidth: innerWidth, innerHeight: innerHeight, highlightedSeries: highlightedSeries !== null && highlightedSeries !== void 0 ? highlightedSeries : null, highlightedGroupIndex: highlightedGroupIndex, horizontalBars: horizontalBars, stackedBars: stackedBars, isGroupNavigation: isGroupNavigation, visibleSeries: visibleSeries, xScale: xScale, yScale: yScale, onLineFocus: lineSeriesProps.onFocus, onLineBlur: onBlur, onLineKeyDown: lineSeriesProps.onKeyDown, focusedSeriesRef: focusedSeriesRef }),
                    emphasizeBaselineAxis && (React.createElement(EmphasizedBaseline, { yScale: yScale, horizontalBars: horizontalBars, innerWidth: innerWidth, innerHeight: innerHeight })),
                    React.createElement(VerticalMarkers, { innerHeight: innerHeight, verticalMarkerLeft: verticalLineX, highlightedPoint: highlightedPoint, isGroupNavigation: isGroupNavigation, scaledSeries: scaledSeries, highlightedPointRef: highlightedPointRef, verticalMarkerRef: verticalMarkerRef }),
                    React.createElement("rect", __assign({}, focusVisible, { "aria-hidden": "true", ref: focusOutlineRef, className: clsx(styles['focus-outline'], (_c = {},
                            _c[styles['focus-outline--active']] = !!highlightedPoint,
                            _c)), rx: "2", height: 8, width: 8, x: 0, y: 0 })),
                    isGroupNavigation && (React.createElement("g", { className: clsx((_d = {},
                            _d[styles['bar-groups--pinned']] = isPopoverOpen && isPopoverPinned,
                            _d)), role: "application", ref: barGroupsRef }, xScale.isCategorical() &&
                        barGroups.map(function (_a, i) {
                            var x = _a.x, isValid = _a.isValid;
                            var OUTLINE_PADDING = 2;
                            var position = xScale.d3Scale(x);
                            var attr = horizontalBars
                                ? {
                                    x: -OUTLINE_PADDING,
                                    y: isValid ? (position || 0) - OUTLINE_PADDING : 0,
                                    width: innerWidth + OUTLINE_PADDING * 2,
                                    height: xScale.d3Scale.bandwidth() + OUTLINE_PADDING * 2
                                }
                                : {
                                    x: isValid ? (position || 0) - OUTLINE_PADDING : 0,
                                    y: -OUTLINE_PADDING,
                                    width: xScale.d3Scale.bandwidth() + OUTLINE_PADDING * 2,
                                    height: innerHeight + OUTLINE_PADDING * 2
                                };
                            var tabIndex = highlightedGroupIndex === i || (highlightedGroupIndex === null && i === 0) ? 0 : -1;
                            return (React.createElement("rect", __assign({}, attr, focusVisible, barGroupProps, { key: "group-" + i, className: styles['bar-groups__group'], "data-group": i, rx: 2, "aria-label": xTickFormatter ? xTickFormatter(x) : x.toString(), tabIndex: tabIndex, focusable: tabIndex === 0, role: "button", "aria-expanded": isPopoverPinned, onBlur: onBlur })));
                        })))),
                React.createElement(BottomLabels, { ticks: horizontalBars ? yTicks : xTicks, scale: horizontalBars ? yScale : xScale, tickFormatter: horizontalBars ? yTickFormatter : xTickFormatter, title: horizontalBars ? yTitle : xTitle, ariaRoleDescription: horizontalBars ? i18nStrings.yAxisAriaRoleDescription : i18nStrings.xAxisAriaRoleDescription, horizontalBars: horizontalBars, width: innerWidth }),
                React.createElement(AxisLabel, { title: horizontalBars ? yTitle : xTitle, position: "bottom", horizontalBars: !!horizontalBars })),
            isPopoverOpen && popoverProps && (React.createElement(ChartPopover, __assign({}, popoverProps, { dismissAriaLabel: i18nStrings.detailPopoverDismissAriaLabel, container: containerRef.current, size: detailPopoverSize }),
                React.createElement(ChartSeriesDetails, { details: popoverDetails }))))));
}
//# sourceMappingURL=chart-container.js.map